import { ApiClient } from '../apiClient';
import {
  BlogPost,
  PaginatedBlogPosts,
  BlogFilters,
  PaginationQuery,
  BlogSitemapEntry,
  Author,
  AuthorSitemapEntry,
} from '../../types/blog';

/**
 * Blog Service - handles public blog operations
 */
export class BlogService {
  constructor(private apiClient: ApiClient) {}

  /**
   * Get published blog posts with pagination
   */
  async getPublishedPosts(query: PaginationQuery = {}, lang?: string): Promise<PaginatedBlogPosts> {
    try {
      const params = {
        page: query.page || 0,
        limit: query.limit || 10,
        ...(query.search && { search: query.search })
      };

      const url = lang ? `/blog/posts?lang=${lang}` : '/blog/posts';
      const result = await this.apiClient.post<PaginatedBlogPosts>(url, params);
      return result;
    } catch (error) {
      console.error('Error fetching published blog posts:', error);
      return {
        items: [],
        total: 0,
        totalPages: 0,
        page: 0,
        limit: 10
      };
    }
  }

  /**
   * Get blog post by slug
   */
  async getPostBySlug(slug: string, lang?: string): Promise<BlogPost | null> {
    try {
      const queryParam = lang ? `?lang=${lang}` : '';
      const result = await this.apiClient.get<BlogPost>(`/blog/posts/slug/${encodeURIComponent(slug)}${queryParam}`);
      return result;
    } catch (error) {
      console.error('Error fetching blog post by slug:', error);
      return null;
    }
  }

  /**
   * Register a blog post view (beacon). Decoupled from the cached post read.
   */
  async registerView(slug: string): Promise<void> {
    return this.apiClient.post<void>(`/blog/posts/slug/${encodeURIComponent(slug)}/view`);
  }

  async getPostHreflangSlugs(slug: string): Promise<Record<string, string>> {
    try {
      return await this.apiClient.get<Record<string, string>>(`/blog/posts/slug/${encodeURIComponent(slug)}/hreflang`);
    } catch (error) {
      console.error('Error fetching blog post hreflang slugs:', error);
      return {};
    }
  }

  // No catch: swallowing errors into [] turns API failures into empty
  // sitemaps ("no URLs"). The sitemap routes translate a throw into a 503.
  async getSitemapPosts(): Promise<BlogSitemapEntry[]> {
    return await this.apiClient.get<BlogSitemapEntry[]>('/blog/posts/sitemap');
  }

  /**
   * Search published blog posts
   */
  async searchPosts(query: string, options: Omit<BlogFilters, 'search'> = {}, lang?: string): Promise<PaginatedBlogPosts> {
    try {
      const params = {
        page: options.page || 0,
        limit: options.limit || 10,
        search: query,
        ...(options.author && { author: options.author }),
        ...(options.tags && { tags: options.tags })
      };

      const url = lang ? `/blog/posts?lang=${lang}` : '/blog/posts';
      const result = await this.apiClient.post<PaginatedBlogPosts>(url, params);
      return result;
    } catch (error) {
      console.error('Error searching blog posts:', error);
      return {
        items: [],
        total: 0,
        totalPages: 0,
        page: 0,
        limit: 10
      };
    }
  }

  /**
   * Get posts by tag
   */
  async getPostsByTag(tag: string, options: Omit<BlogFilters, 'tags'> = {}, lang?: string): Promise<PaginatedBlogPosts> {
    try {
      const params = {
        page: options.page || 0,
        limit: options.limit || 10,
        tags: [tag],
        ...(options.author && { author: options.author }),
        ...(options.search && { search: options.search })
      };

      const url = lang ? `/blog/posts?lang=${lang}` : '/blog/posts';
      const result = await this.apiClient.post<PaginatedBlogPosts>(url, params);
      return result;
    } catch (error) {
      console.error('Error fetching posts by tag:', error);
      return {
        items: [],
        total: 0,
        totalPages: 0,
        page: 0,
        limit: 10
      };
    }
  }

  /**
   * Get posts by author
   */
  async getPostsByAuthor(author: string, options: Omit<BlogFilters, 'author'> = {}, lang?: string): Promise<PaginatedBlogPosts> {
    try {
      const params = {
        page: options.page || 0,
        limit: options.limit || 10,
        author,
        ...(options.tags && { tags: options.tags }),
        ...(options.search && { search: options.search })
      };

      const url = lang ? `/blog/posts?lang=${lang}` : '/blog/posts';
      const result = await this.apiClient.post<PaginatedBlogPosts>(url, params);
      return result;
    } catch (error) {
      console.error('Error fetching posts by author:', error);
      return {
        items: [],
        total: 0,
        totalPages: 0,
        page: 0,
        limit: 10
      };
    }
  }

  /**
   * Get all authors (public)
   */
  async getAuthors(lang?: string): Promise<Author[]> {
    try {
      const url = lang ? `/blog/authors?lang=${lang}` : '/blog/authors';
      return await this.apiClient.get<Author[]>(url);
    } catch (error) {
      console.error('Error fetching authors:', error);
      return [];
    }
  }

  /**
   * Get a single author by slug
   */
  async getAuthorBySlug(slug: string, lang?: string): Promise<Author | null> {
    try {
      const query = lang ? `?lang=${lang}` : '';
      return await this.apiClient.get<Author>(
        `/blog/authors/${encodeURIComponent(slug)}${query}`,
      );
    } catch (error) {
      console.error('Error fetching author by slug:', error);
      return null;
    }
  }

  /**
   * Get paginated posts for a given author slug
   */
  async getPostsByAuthorSlug(
    slug: string,
    options: PaginationQuery = {},
    lang?: string,
  ): Promise<PaginatedBlogPosts> {
    try {
      const params = {
        page: options.page || 0,
        limit: options.limit || 10,
      };
      const query = lang ? `?lang=${lang}` : '';
      return await this.apiClient.post<PaginatedBlogPosts>(
        `/blog/authors/slug/${encodeURIComponent(slug)}/posts${query}`,
        params,
      );
    } catch (error) {
      console.error('Error fetching posts by author slug:', error);
      return {
        items: [],
        total: 0,
        totalPages: 0,
        page: 0,
        limit: 10,
      };
    }
  }

  /**
   * Get all authors for sitemap
   */
  // No catch - see getSitemapPosts.
  async getSitemapAuthors(): Promise<AuthorSitemapEntry[]> {
    return await this.apiClient.get<AuthorSitemapEntry[]>(
      '/blog/authors/sitemap',
    );
  }
}
